/**
 * AI Tools Registry
 *
 * Central registry for all available tools. Provides methods to:
 * - Register new tools
 * - Get tool definitions by name
 * - Generate JSON schema for system prompts
 * - Filter tools by category
 */

import type { ToolDefinition, ToolSchema, ToolCategory } from './types';

/**
 * Central registry storing all tool definitions
 */
class ToolRegistry {
  private tools: Map<string, ToolDefinition> = new Map();

  /**
   * Register a tool in the registry
   */
  register(tool: ToolDefinition): void {
    if (this.tools.has(tool.name)) {
      console.warn(`Tool "${tool.name}" is already registered. Overwriting.`);
    }
    this.tools.set(tool.name, tool);
  }

  /**
   * Register multiple tools at once
   */
  registerAll(tools: ToolDefinition[]): void {
    tools.forEach(tool => this.register(tool));
  }

  /**
   * Get a tool definition by name
   */
  getTool(name: string): ToolDefinition | undefined {
    return this.tools.get(name);
  }

  /**
   * Check if a tool exists
   */
  hasTool(name: string): boolean {
    return this.tools.has(name);
  }

  /**
   * Get all registered tools
   */
  getAllTools(): ToolDefinition[] {
    return Array.from(this.tools.values());
  }

  /**
   * Get tools filtered by category
   */
  getToolsByCategory(category: ToolCategory): ToolDefinition[] {
    return this.getAllTools().filter(tool => tool.category === category);
  }

  /**
   * Get tools that require confirmation
   */
  getToolsRequiringConfirmation(): ToolDefinition[] {
    return this.getAllTools().filter(tool => tool.requiresConfirmation);
  }

  /**
   * Check if a tool requires confirmation
   */
  requiresConfirmation(toolName: string): boolean {
    const tool = this.getTool(toolName);
    return tool?.requiresConfirmation ?? false;
  }

  /**
   * Generate schema array for system prompt inclusion
   */
  getToolsSchema(): ToolSchema[] {
    return this.getAllTools().map(tool => ({
      name: tool.name,
      description: tool.description,
      parameters: {
        type: 'object' as const,
        properties: (tool.parameters.properties || {}) as Record<string, unknown>,
        required: tool.parameters.required as string[] | undefined,
      },
      requiresConfirmation: tool.requiresConfirmation,
    }));
  }

  /**
   * Generate schema for specific categories only
   */
  getToolsSchemaByCategories(categories: ToolCategory[]): ToolSchema[] {
    return this.getAllTools()
      .filter(tool => categories.includes(tool.category))
      .map(tool => ({
        name: tool.name,
        description: tool.description,
        parameters: {
          type: 'object' as const,
          properties: (tool.parameters.properties || {}) as Record<string, unknown>,
          required: tool.parameters.required as string[] | undefined,
        },
        requiresConfirmation: tool.requiresConfirmation,
      }));
  }

  /**
   * Get tool names as a simple list
   */
  getToolNames(): string[] {
    return Array.from(this.tools.keys());
  }

  /**
   * Get a summary of registered tools by category
   */
  getSummary(): Record<ToolCategory, string[]> {
    const summary: Partial<Record<ToolCategory, string[]>> = {};

    this.getAllTools().forEach(tool => {
      if (!summary[tool.category]) {
        summary[tool.category] = [];
      }
      summary[tool.category]!.push(tool.name);
    });

    return summary as Record<ToolCategory, string[]>;
  }

  /**
   * Clear all registered tools (useful for testing)
   */
  clear(): void {
    this.tools.clear();
  }

  /**
   * Get count of registered tools
   */
  get size(): number {
    return this.tools.size;
  }
}

/**
 * Singleton instance of the tool registry
 */
export const toolRegistry = new ToolRegistry();

/**
 * Helper function to register tools
 */
export function registerTools(tools: ToolDefinition[]): void {
  toolRegistry.registerAll(tools);
}

/**
 * Helper function to get a tool
 */
export function getTool(name: string): ToolDefinition | undefined {
  return toolRegistry.getTool(name);
}

/**
 * Helper function to get all tools schema
 */
export function getToolsSchema(): ToolSchema[] {
  return toolRegistry.getToolsSchema();
}

/**
 * Helper function to check if tool requires confirmation
 */
export function toolRequiresConfirmation(name: string): boolean {
  return toolRegistry.requiresConfirmation(name);
}
